import { useQueryClient } from "@tanstack/react-query";
import type Decimal from "decimal.js-light";
import { useCallback } from "react";

import { invalidateAbsenceQueries } from "~/modules/absence/api/absence/invalidateAbsenceQueries.ts";
import type { WorkingSchedule } from "~/modules/absence/api/workingSchedule/workingScheduleTypes.ts";
import CreateAbsenceForm from "~/modules/absence/components/AbsencesView/components/CreateAbsenceForm";
import useAbsenceTypeOptions from "~/modules/absence/hooks/useAbsenceTypeOptions.tsx";

export type CreateAbsencePanelProps = {
	onClose: () => void;
	remainingVacationDays: Decimal;
	staffMemberId: string;
	userCanManageLongTermAbsences: boolean;
	workingSchedules: WorkingSchedule[];
	presetAbsenceTypeId?: string;
	presetDate?: Date;
};

/**
 * The content of the create sidebar without the `<Sidebar>` itself, so that a sidebar can compose it with
 * other content, for example the create sidebar of the dashboard calendar.
 */
export function CreateAbsencePanel({
	onClose,
	remainingVacationDays,
	staffMemberId,
	userCanManageLongTermAbsences,
	workingSchedules,
	presetAbsenceTypeId,
	presetDate,
}: CreateAbsencePanelProps) {
	const queryClient = useQueryClient();
	const handleSuccess = useCallback(
		async () => {
			await invalidateAbsenceQueries(queryClient, staffMemberId);
			onClose();
		},
		[onClose, queryClient, staffMemberId],
	);

	const absenceTypeOptions = useAbsenceTypeOptions();

	return (
		<CreateAbsenceForm
			absenceTypeSelectOptions={absenceTypeOptions}
			onSuccess={handleSuccess}
			remainingVacationDays={remainingVacationDays}
			onCancel={onClose}
			workingSchedules={workingSchedules}
			staffMemberId={staffMemberId}
			userCanManageLongTermAbsences={userCanManageLongTermAbsences}
			presetAbsenceTypeId={presetAbsenceTypeId}
			presetDate={presetDate}
		/>
	);
}
